/**
 * MSAL Configuration Factory for Entra External ID (CIAM)
 *
 * Creates a correctly-configured MSAL Configuration object.
 * Encodes CIAM-specific patterns:
 * - ciamlogin.com authority (NOT b2clogin.com)
 * - navigateToLoginRequestUrl: false (prevents redirect loops)
 * - OIDC-only scopes (CIAM doesn't support custom API scopes)
 */
import { type Configuration, LogLevel } from '@azure/msal-browser';
import type { MsalConfigOptions } from './types';

export function createMsalConfig(options: MsalConfigOptions): Configuration {
  const { tenantName, tenantId, clientId, redirectUri, postLogoutRedirectUri } = options;
  // eslint-disable-next-line unicorn/no-typeof-undefined
  const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
  const debug = options.debug ?? isDev;

  return {
    auth: {
      clientId,
      // CIAM uses ciamlogin.com, NOT b2clogin.com
      authority: `https://${tenantName}.ciamlogin.com/${tenantId}`,
      knownAuthorities: [`${tenantName}.ciamlogin.com`],
      redirectUri,
      postLogoutRedirectUri,
      // CRITICAL: Must be false to prevent redirect loops on /auth/callback
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          switch (level) {
            case LogLevel.Error:
              // eslint-disable-next-line no-console
              console.error(message);
              break;
            case LogLevel.Warning:
              // eslint-disable-next-line no-console
              console.warn(message);
              break;
            case LogLevel.Info:
              // eslint-disable-next-line no-console
              if (debug) console.info(message);
              break;
            case LogLevel.Verbose:
              // eslint-disable-next-line no-console
              if (debug) console.debug(message);
              break;
          }
        },
        logLevel: debug ? LogLevel.Info : LogLevel.Error,
      },
    },
  };
}

/**
 * Login request scopes.
 * CIAM only supports OIDC scopes — no custom API scopes.
 * The ID token (not access token) is used for API auth.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};
